import { computed, ref, watch, type Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useRouterQueryPagination } from '@/composables/useRouterQueryPagination'
import { ManageTopProductsRankingOrderBy, SortDirectionEnum, useReviewsTopProductsRankingQuery } from '@/gql/reviews'
import { checkEnumValue } from '@/helpers/checkEnumValue'
import type { SortDirection, SortParams } from '@/ui/TableLight/types'

import type { TopProductsRankingEntry } from './types'
import { topProductsRankingPageForPosition } from './useTopProductsRankingPage'

export function useTopProductsRanking(productSlug: Ref<string>) {
  const route = useRoute()
  const router = useRouter()
  const { page: rawPage, perPage: rawPerPage, updatePage, updatePerPage } = useRouterQueryPagination()

  const page = computed(() => (Number.isInteger(rawPage.value) && rawPage.value >= 1 ? rawPage.value : 1))
  const perPage = computed(() =>
    Number.isInteger(rawPerPage.value) && rawPerPage.value >= 1 && rawPerPage.value <= 200 ? rawPerPage.value : 10,
  )

  const sortBy = computed(() => checkEnumValue(route.query.sortBy, ManageTopProductsRankingOrderBy, ManageTopProductsRankingOrderBy.Rank))
  const sortDirection = computed(() => checkEnumValue(route.query.sortDirection, SortDirectionEnum, SortDirectionEnum.Asc))
  const search = computed(() => (typeof route.query.q === 'string' ? route.query.q : ''))

  const variables = computed(() => ({
    productSlug: productSlug.value,
    orderBy: sortBy.value,
    orderDir: sortDirection.value,
    page: page.value,
    perPage: perPage.value,
    search: search.value || undefined,
  }))

  const { result, loading, error, refetch } = useReviewsTopProductsRankingQuery(variables, {
    clientId: 'reviews',
    keepPreviousResult: true,
  })

  const ranking = computed(() => result.value?.manage?.topProductsRanking)
  const entries = computed<TopProductsRankingEntry[]>(() => ranking.value?.results ?? [])
  const total = computed(() => ranking.value?.pagination.totalCount ?? 0)
  const year = computed(() => ranking.value?.year ?? undefined)

  const ownEntry = ref<TopProductsRankingEntry | undefined>()
  const anchorPending = ref(route.query.page === undefined)

  watch(productSlug, () => {
    ownEntry.value = undefined
    anchorPending.value = route.query.page === undefined
  })

  watch(ranking, (value) => {
    if (!value) return
    if (value.pinned) ownEntry.value = value.pinned
    if (!anchorPending.value) return
    anchorPending.value = false
    const position = value.pinned?.position
    if (!position) return
    const target = topProductsRankingPageForPosition(position, perPage.value).page
    if (target !== page.value) router.replace({ query: { ...route.query, page: target === 1 ? undefined : String(target) } })
  })

  function setPerPage(value: number) {
    anchorPending.value = true
    updatePerPage(value)
  }

  function setSort(params: SortParams<ManageTopProductsRankingOrderBy, SortDirection>) {
    router.push({ query: { ...route.query, sortBy: params.sortBy, sortDirection: params.sortDirection, page: undefined } })
  }

  function setSearch(term: string) {
    anchorPending.value = term === ''
    router.push({ query: { ...route.query, q: term || undefined, page: undefined } })
  }

  function goToOwnRow() {
    const position = ranking.value?.pinned?.position
    if (position) {
      updatePage(topProductsRankingPageForPosition(position, perPage.value).page)
      return
    }
    anchorPending.value = true
    router.push({ query: { ...route.query, q: undefined, page: undefined } })
  }

  return {
    entries,
    ownEntry,
    total,
    year,
    page,
    perPage,
    sortBy,
    sortDirection,
    search,
    loading,
    error,
    refetch,
    setPage: updatePage,
    setPerPage,
    setSort,
    setSearch,
    goToOwnRow,
  }
}
